import isEqual from 'lodash.isequal'

import {
  BaseSymconObject,
  SymconObjectType,
  SymconVariableType,
} from './interfaces'
import { KnockautHandler } from './knockautHandler'

export interface Days {
  monday: boolean
  tuesday: boolean
  wednesday: boolean
  thursday: boolean
  friday: boolean
  saturday: boolean
  sunday: boolean
}

export enum EventConditionComparison {
  EQUAL = 0, //	==
  NOT_EQUAL = 1, //	!=
  GREATER = 2, //	>
  GREATER_EQ = 3, //	>=
  LESS = 4, //	<
  LESS_EQ = 5, //	<=
}

export interface EventDate {
  day: number
  month: number
  year: number
}

export interface EventTime {
  hour: number
  minute: number
  second: number
}

export enum CyclicDateType {
  NONE = 0, //	Kein Datumstyp. Tägliche Ausführung.
  ONCE = 1, //	Einmalig. IPS_SetEventCyclicDateFrom für Zieldatum
  DAILY = 2, //	Täglich
  WEEKLY = 3, //	Wöchentlich
  MONTHLY = 4, //	Monatlich
  YEARLY = 5, //Jährlich
}

export enum CyclicTimeType {
  ONCE = 0, //	Einmalig IPS_SetEventCyclicTimeFrom für Zielzeit
  EVERY_SECOND = 1, //	Sekündlich
  EVERY_MINUTE = 2, //	Minütlich
  HOURLY = 3, //	Stündlich
}

export enum EventType {
  TRIGGERED = 0, // Ausgelöst
  PERIODIC = 1, // Zyklisch
  WEEKLY_SCHEDULE = 2, // Wochenplan
  MANUAL = -1, // Manuelle Ausführung von Ablaufplänen per Link
}

export enum EventTriggerType {
  VARIABLE_UPDATE = 0, // Bei Variablenaktualisierung
  VARIABLE_CHANGE = 1, // Bei Variablenänderung
  UPPER_LIMIT = 2, // Bei Grenzüberschreitung. Grenzwert wird über IPS_SetEventTriggerValue festgelegt
  LOWER_LIMIT = 3, // Bei Grenzunterschreitung. Grenzwert wird über IPS_SetEventTriggerValue festgelegt
  SPECIFIC_VALUE = 4, // Bei bestimmtem Wert. Wert wird über IPS_SetEventTriggerValue festgelegt
}

export enum ConditionRuleType {
  DATE = 'Datum',
  DAY_OF_THE_WEEK = 'Wochentag',
  TIME = 'Uhrzeit',
  VARIABLE = 'Variable',
}

export interface EventCondition {
  id: number
  operation: 0 | 1
  parentID: number
  rules: {
    date: {
      comparison: EventConditionComparison
      id: number
      value: EventDate
    }[]
    dayOfTheWeek: {
      comparison: EventConditionComparison
      id: number
      value: number
    }[]
    time: {
      comparison: EventConditionComparison
      id: number
      value: EventTime
    }[]
    variable: {
      comparison: EventConditionComparison
      id: number
      value: number
      variableID: number
    }[]
  }
}

export interface ScheduleAction {
  color: number
  id: number
  name: string
  scriptText: string
}

export interface ScheduleGroupPoint {
  actionID: number
  id: number
  start: EventTime
}

export interface ScheduleGroup {
  days: number
  id: number
  points: ScheduleGroupPoint[]
}

export interface SymconEventData {
  actionID?: string
  actionParameters?: any
  active?: boolean
  conditions?: EventCondition[]
  lastRun?: number
  limit?: number
  nextRun?: number
  script?: string
  type: EventType

  // Trigger Event
  triggerSubsequentExecution?: boolean
  triggerType?: EventTriggerType // Auslöser
  triggerValue?: boolean // Wert
  triggerVariableID?: number // Auslösende Variable

  // Cyclic events
  cyclicDateDay?: number // Datumstage
  cyclicDateDayValue?: number // Datumstagesintervall

  cyclicDateFrom?: EventDate // Startdatum als Array von Tag, Monat, Jahr; 0/0/0 = Immer.
  cyclicDateTo?: EventDate // Enddatum als Array von Tag, Monat, Jahr; 0/0/0 = Nie.
  cyclicDateType?: CyclicDateType // Datumstyp
  cyclicDateValue?: number // Datumsintervall

  cyclicTimeFrom?: EventTime // Startzeit als Array von Stunde, Minute, Sekunde; 0/0/0 = Mitternacht
  cyclicTimeTo?: EventTime // Endzeit als Array von Stunde, Minute, Sekunde; 0/0/0 = Nie.
  cyclicTimeType?: CyclicTimeType // Zeittyp
  cyclicTimeValue?: number // Zeitintervall

  // Schedule Event
  scheduleActions?: ScheduleAction[]
  scheduleGroups?: ScheduleGroup[]
}

export interface KnockautEvent extends BaseSymconObject {
  id: number
  data: SymconEventData
}

export class EventHandler {
  private _knockautHandler: KnockautHandler

  constructor(knockautHandler: KnockautHandler) {
    this._knockautHandler = knockautHandler
  }

  /**
   * Create or update event
   * @param originalModel
   * @param model
   * @param originalConditionRules
   * @param conditionRules
   * @param conditionOperation
   */
  async updateEvent(
    originalModel: KnockautEvent,
    model: KnockautEvent,
    originalConditionRules: any[],
    conditionRules: any[],
    conditionOperation: 0 | 1
  ) {
    let isNewEvent = false
    if (!model.id) {
      const id = await this.createEvent(model.data.type)
      model.id = id
      isNewEvent = true
    }
    const eventId = model.id
    if (isNewEvent || originalModel.name !== model.name) {
      await this._knockautHandler.setNameSymconObject(model.id, model.name)
    }
    if (model.data.type === EventType.TRIGGERED) {
      if (
        isNewEvent ||
        originalModel.data.triggerType !== model.data.triggerType ||
        originalModel.data.triggerVariableID !== model.data.triggerVariableID
      ) {
        await this.setEventTrigger(
          eventId,
          model.data.triggerType,
          model.data.triggerVariableID
        )
      }
      if (
        isNewEvent ||
        originalModel.data.triggerSubsequentExecution !==
          model.data.triggerSubsequentExecution
      ) {
        await this.setEventTriggerSubsequentExecution(
          eventId,
          model.data.triggerSubsequentExecution
        )
      }
      if (
        model.data.triggerValue !== null &&
        (isNewEvent ||
          originalModel.data.triggerValue !== model.data.triggerValue)
      ) {
        const triggerVariable = this._knockautHandler.getObjectById(
          model.data.triggerVariableID
        )
        await this.setEventTriggerValue(
          eventId,
          model.data.triggerValue,
          triggerVariable.data.type
        )
      }
    } else if (model.data.type === EventType.PERIODIC) {
      await this.setEventCyclic(
        eventId,
        model.data.cyclicDateType,
        model.data.cyclicDateValue,
        model.data.cyclicDateDay,
        model.data.cyclicDateDayValue,
        model.data.cyclicTimeType,
        model.data.cyclicTimeValue
      )
      if (
        isNewEvent ||
        !isEqual(originalModel.data.cyclicDateFrom, model.data.cyclicDateFrom)
      ) {
        await this.setEventCyclicDateFrom(eventId, model.data.cyclicDateFrom)
      }
      if (
        isNewEvent ||
        !isEqual(originalModel.data.cyclicDateTo, model.data.cyclicDateTo)
      ) {
        await this.setEventCyclicDateTo(eventId, model.data.cyclicDateTo)
      }
      if (
        isNewEvent ||
        !isEqual(originalModel.data.cyclicTimeFrom, model.data.cyclicTimeFrom)
      ) {
        await this.setEventCyclicTimeFrom(eventId, model.data.cyclicTimeFrom)
      }
      if (
        isNewEvent ||
        !isEqual(originalModel.data.cyclicTimeTo, model.data.cyclicTimeTo)
      ) {
        await this.setEventCyclicTimeTo(eventId, model.data.cyclicTimeTo)
      }
    } else if (model.data.type === EventType.WEEKLY_SCHEDULE) {
      // TODO: Not supported currently
      throw new Error('Weekly Schedule not supported yet.')
    }

    // TODO: Currently we only support one root condition
    const conditionId = 0

    // Set initial condition
    await this.setEventCondition(eventId, conditionId, 0, conditionOperation)

    // First we delete all originalConditionRules, add then we add the new ones
    // This makes it easier when rules are edited and/or deleted and re-created inside the same session
    for (const rule of originalConditionRules) {
      if (rule.type === ConditionRuleType.DATE) {
        // Setting either year, month or day to -1 will delete rule
        rule.value.year = -1
        await this.setEventConditionDateRule(
          eventId,
          conditionId,
          rule.id,
          rule.comparison,
          rule.value
        )
      } else if (rule.type === ConditionRuleType.DAY_OF_THE_WEEK) {
        await this.setEventConditionDayOfTheWeekRule(
          eventId,
          conditionId,
          rule.id,
          rule.comparison,
          0
        )
      } else if (rule.type === ConditionRuleType.TIME) {
        // Setting either hour, minute or second to -1 will delete rule
        rule.value.hour = -1
        await this.setEventConditionTimeRule(
          eventId,
          conditionId,
          rule.id,
          rule.comparison,
          rule.value
        )
      } else if (rule.type === ConditionRuleType.VARIABLE) {
        await this.setEventConditionVariableRule(
          eventId,
          conditionId,
          rule.id,
          0,
          rule.comparison,
          rule.value
        )
      } else {
        throw new Error(`Unknown rule type ${rule.type}`)
      }
    }

    let uniqueRuleId = 0
    for (const rule of conditionRules) {
      if (rule.type === ConditionRuleType.DATE) {
        await this.setEventConditionDateRule(
          eventId,
          conditionId,
          uniqueRuleId,
          rule.comparison,
          rule.value
        )
      } else if (rule.type === ConditionRuleType.DAY_OF_THE_WEEK) {
        await this.setEventConditionDayOfTheWeekRule(
          eventId,
          conditionId,
          uniqueRuleId,
          rule.comparison,
          rule.value
        )
      } else if (rule.type === ConditionRuleType.TIME) {
        await this.setEventConditionTimeRule(
          eventId,
          conditionId,
          uniqueRuleId,
          rule.comparison,
          rule.value
        )
      } else if (rule.type === ConditionRuleType.VARIABLE) {
        await this.setEventConditionVariableRule(
          eventId,
          conditionId,
          uniqueRuleId,
          rule.variableID,
          rule.comparison,
          rule.value
        )
      }
      uniqueRuleId += 1
    }

    // Set parent
    if (isNewEvent || originalModel.parentID !== model.parentID) {
      await this._knockautHandler.setParentId(eventId, model.parentID)
    }

    // Set script
    if (isNewEvent || originalModel.data.script !== model.data.script) {
      await this.setEventScript(eventId, model.data.script)
    }

    // Set active
    if (isNewEvent || originalModel.data.active !== model.data.active) {
      await this.setEventActive(eventId, model.data.active)
    }
    await this._knockautHandler.refreshTree()
  }

  /** PERIODIC **/
  async setEventCyclic(
    eventId: number,
    cyclicDateType: number,
    cyclicDateValue: number,
    cyclicDateDay: number,
    cyclicDateDayValue: number,
    cyclicTimeType: number,
    cyclicTimeValue: number
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventCyclic',
      [
        eventId,
        cyclicDateType,
        cyclicDateValue,
        cyclicDateDay,
        cyclicDateDayValue,
        cyclicTimeType,
        cyclicTimeValue,
      ],
      true
    )
  }
  async setEventCyclicDateFrom(
    eventId: number,
    date: EventDate
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventCyclicDateFrom',
      [eventId, date.day, date.month, date.year],
      true
    )
  }
  async setEventCyclicDateTo(
    eventId: number,
    date: EventDate
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventCyclicDateTo',
      [eventId, date.day, date.month, date.year],
      true
    )
  }
  async setEventCyclicTimeFrom(
    eventId: number,
    time: EventTime
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventCyclicTimeFrom',
      [eventId, time.hour, time.minute, time.second],
      true
    )
  }
  async setEventCyclicTimeTo(
    eventId: number,
    time: EventTime
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventCyclicTimeTo',
      [eventId, time.hour, time.minute, time.second],
      true
    )
  }

  /** TRIGGER **/
  async setEventTrigger(
    eventId: number,
    triggerType: number,
    triggerVariableId: number
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventTrigger',
      [eventId, triggerType, triggerVariableId],
      true
    )
  }
  async setEventTriggerSubsequentExecution(
    eventId: number,
    triggerSubsequent: boolean
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventTriggerSubsequentExecution',
      [eventId, triggerSubsequent],
      true
    )
  }
  async setEventTriggerValue(
    eventId: number,
    value: boolean | number | string,
    triggerType: SymconVariableType
  ): Promise<boolean> {
    if (
      triggerType === SymconVariableType.INTEGER &&
      typeof value === 'string'
    ) {
      value = parseInt(value)
    } else if (
      triggerType === SymconVariableType.FLOAT &&
      typeof value === 'string'
    ) {
      value = parseFloat(value)
    } else if (
      triggerType === SymconVariableType.BOOLEAN &&
      (typeof value === 'string' || typeof value === 'number')
    ) {
      if (value === '1' || value === 1) {
        value = true
      } else if (value === '0' || value === 0) {
        value = false
      }
    }
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventTriggerValue',
      [eventId, value],
      true
    )
  }

  /** CONDITION **/
  async setEventCondition(
    eventId: number,
    conditionId: number,
    parentID: number,
    operation: number
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventCondition',
      [eventId, conditionId, parentID, operation],
      true
    )
  }
  async setEventConditionDateRule(
    eventId: number,
    conditionId: number,
    ruleId: number,
    comparison: number,
    date: EventDate
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventConditionDateRule',
      [
        eventId,
        conditionId,
        ruleId,
        comparison,
        date.day,
        date.month,
        date.year,
      ],
      true
    )
  }
  async setEventConditionDayOfTheWeekRule(
    eventId: number,
    conditionId: number,
    ruleId: number,
    comparison: number,
    weekDay: number
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventConditionDayOfTheWeekRule',
      [eventId, conditionId, ruleId, comparison, weekDay],
      true
    )
  }
  async setEventConditionTimeRule(
    eventId: number,
    conditionId: number,
    ruleId: number,
    comparison: number,
    time: EventTime
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventConditionTimeRule',
      [
        eventId,
        conditionId,
        ruleId,
        comparison,
        time.hour,
        time.minute,
        time.second,
      ],
      true
    )
  }
  async setEventConditionVariableRule(
    eventId: number,
    conditionId: number,
    ruleId: number,
    variableID: number,
    comparison: number,
    value: any
  ): Promise<boolean> {
    // if variableID is 0, we delete it
    // https://www.symcon.de/service/dokumentation/befehlsreferenz/ereignisverwaltung/ips-seteventconditionvariablerule/
    if (variableID !== 0) {
      const variable = this._knockautHandler.getObjectById(variableID)
      if (
        variable.data.type === SymconVariableType.INTEGER &&
        typeof value === 'string'
      ) {
        value = parseInt(value)
      } else if (
        variable.data.type === SymconVariableType.FLOAT &&
        typeof value === 'string'
      ) {
        value = parseFloat(value)
      } else if (
        variable.data.type === SymconVariableType.BOOLEAN &&
        (typeof value === 'string' || typeof value === 'number')
      ) {
        if (value === '1' || value === 1) {
          value = true
        } else if (value === '0' || value === 0) {
          value = false
        }
      }
    }

    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventConditionVariableRule',
      [eventId, conditionId, ruleId, variableID, comparison, value],
      true
    )
  }

  /** SCHEDULE **/
  async setEventScheduleAction(
    eventId: number,
    actionId: number,
    name: string,
    color: string,
    scriptContent: string
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventScheduleAction',
      [eventId, actionId, name, color, scriptContent],
      true
    )
  }
  async setEventScheduleGroup(
    eventId: number,
    groupId: number,
    days: number
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventScheduleGroup',
      [eventId, groupId, days],
      true
    )
  }
  async setEventScheduleGroupPoint(
    eventId: number,
    groupId: number,
    groupPointId: number,
    time: EventTime,
    actionId: number
  ): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventScheduleGroupPoint',
      [
        eventId,
        groupId,
        groupPointId,
        time.hour,
        time.minute,
        time.second,
        actionId,
      ],
      true
    )
  }

  /** OTHER */
  async createEvent(type: EventType): Promise<number> {
    return this._knockautHandler.parseKnockautResponse('IPS_CreateEvent', [
      type,
    ])
  }
  async setEventActive(eventId: number, active: boolean): Promise<boolean> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventActive',
      [eventId, active],
      true
    )
  }
  async setEventScript(eventId: number, content: string): Promise<number> {
    return this._knockautHandler.parseKnockautResponse(
      'IPS_SetEventScript',
      [eventId, content],
      true
    )
  }

  parseConditions(conditions: EventCondition[]) {
    const conditionData = []
    for (const condition of conditions) {
      if (condition.rules.date) {
        for (const dateRule of condition.rules.date) {
          conditionData.push({
            ...dateRule,
            type: ConditionRuleType.DATE,
          })
        }
      }
      if (condition.rules.dayOfTheWeek) {
        for (const dateRule of condition.rules.dayOfTheWeek) {
          conditionData.push({
            ...dateRule,
            type: ConditionRuleType.DAY_OF_THE_WEEK,
          })
        }
      }
      if (condition.rules.time) {
        for (const timeRule of condition.rules.time) {
          conditionData.push({
            ...timeRule,
            type: ConditionRuleType.TIME,
          })
        }
      }
      if (condition.rules.variable) {
        for (const variableRule of condition.rules.variable) {
          conditionData.push({
            ...variableRule,
            type: ConditionRuleType.VARIABLE,
          })
        }
      }
    }
    return conditionData
  }

  getConditionRuleName(
    ruleType: ConditionRuleType,
    variableID?: number
  ): string {
    if (ruleType === ConditionRuleType.VARIABLE) {
      const variable = this._knockautHandler.getObjectById(variableID)
      if (variable) {
        const parent = this._knockautHandler.getObjectById(variable.parentID)
        return `${variable.name} (${parent.name})`
      } else {
        return `Ungültige Variable`
      }
    }
    switch (ruleType) {
      case ConditionRuleType.DATE:
        return 'Aktuelles Datum'
      case ConditionRuleType.DAY_OF_THE_WEEK:
        return 'Aktueller Wochentag'
      case ConditionRuleType.TIME:
        return 'Aktuelle Uhrzeit'
      default:
        break
    }
  }

  getConditionPrettyValue(ruleType: ConditionRuleType, rule: any): string {
    switch (ruleType) {
      case ConditionRuleType.DATE:
        return `${rule.value.day}.${rule.value.month}.${rule.value.year}`
      case ConditionRuleType.DAY_OF_THE_WEEK:
        return getWeekDayName(rule.value)
      case ConditionRuleType.TIME:
        return `${rule.value.hour}:${rule.value.minute}:${rule.value.second}`
      case ConditionRuleType.VARIABLE:
        return rule.value
      default:
        break
    }
  }
}

export function getComparisonName(
  comparison: EventConditionComparison
): string {
  switch (comparison) {
    case EventConditionComparison.EQUAL:
      return '=='
    case EventConditionComparison.NOT_EQUAL:
      return '≠'
    case EventConditionComparison.GREATER:
      return '>'
    case EventConditionComparison.GREATER_EQ:
      return '≥'
    case EventConditionComparison.LESS:
      return '<'
    case EventConditionComparison.LESS_EQ:
      return '≤'
    default:
      break
  }
}

export function enabledDaysToNumber(days: Days): number {
  let sum = 0
  if (days.monday) {
    sum += 1
  }
  if (days.tuesday) {
    sum += 2
  }
  if (days.wednesday) {
    sum += 4
  }
  if (days.thursday) {
    sum += 8
  }
  if (days.friday) {
    sum += 16
  }
  if (days.saturday) {
    sum += 32
  }
  if (days.sunday) {
    sum += 64
  }
  return sum
}

export function cyclicDateDayToEnabledDays(sum: number): Days {
  const days = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  }
  if (sum > 63) {
    sum -= 64
    days.sunday = true
  }
  if (sum > 31) {
    sum -= 32
    days.saturday = true
  }
  if (sum > 15) {
    sum -= 16
    days.friday = true
  }
  if (sum > 7) {
    sum -= 8
    days.thursday = true
  }
  if (sum > 3) {
    sum -= 4
    days.wednesday = true
  }
  if (sum > 1) {
    sum -= 2
    days.tuesday = true
  }
  if (sum > 0) {
    days.monday = true
  }
  return days
}

export function getWeekDayName(day: number): string {
  switch (day) {
    case 1:
      return 'Montag'
    case 2:
      return 'Dienstag'
    case 3:
      return 'Mittwoch'
    case 4:
      return 'Donnerstag'
    case 5:
      return 'Freitag'
    case 6:
      return 'Samstag'
    case 7:
      return 'Sonntag'
    default:
      break
  }
}

export function eventDateFromDate(date: Date): EventDate {
  return {
    day: date.getDay(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
  }
}

export function eventTimeFromDate(date: Date): EventTime {
  return {
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  }
}

export const BASE_KNOCKAUT_EVENT_MODEL: KnockautEvent = {
  id: undefined,
  disabled: false,
  hidden: false,
  icon: '',
  ident: '',
  info: '',
  name: '',
  parentID: 0,
  position: 0,
  readOnly: false,
  summary: '',
  type: SymconObjectType.EVENT,
  data: {
    active: true,
    type: EventType.TRIGGERED,
    script: '',

    triggerVariableID: null,
    triggerSubsequentExecution: false,
    triggerType: EventTriggerType.VARIABLE_CHANGE,
    triggerValue: null,

    cyclicDateDayValue: 1,
    cyclicDateFrom: eventDateFromDate(new Date()),
    cyclicDateTo: eventDateFromDate(new Date()),
    cyclicTimeFrom: eventTimeFromDate(new Date()),
    cyclicTimeTo: eventTimeFromDate(new Date()),
    cyclicDateDay: 0,
    cyclicTimeValue: 0,
    cyclicDateValue: 1,

    conditions: [],
  },
}
